import type { InferInsertModel, InferSelectModel } from 'drizzle-orm';
import {
  boolean,
  integer,
  jsonb,
  pgTable,
  text,
  timestamp,
  uuid,
} from 'drizzle-orm/pg-core';

import { softDelete } from '../columns/soft-delete';
export type FieldType =
  | 'string'
  | 'password'
  | 'number'
  | 'currency'
  | 'boolean'
  | 'date'
  | 'datetime'
  | 'enum'
  | 'relation'
  | 'multi-relation'
  | 'component'
  | 'file'
  | 'avatar'
  | 'email'
  | 'phone';

/** Referência a outro recurso (select de relação, badges N:N). */
export interface RelationRef {
  resource: string;
  displayField: string;
  /** Ordenação das opções no select (ex: events por 'date' desc). Default: a da API (id asc). */
  orderBy?: string;
  order?: 'asc' | 'desc';
}

export const conditionOperatorValues = [
  'equals',
  'notEquals',
  'in',
  'notIn',
  'exists',
  'notExists',
] as const;
export type ConditionOperator = (typeof conditionOperatorValues)[number];

/**
 * Condição sobre os valores do formulário (mesma semântica do `visibleIf`/`enabledIf` do
 * admin antigo). `field` pode ser qualquer campo do form ou `id` (existe = edição/visualização).
 * `in`/`notIn`: `value` com os valores separados por vírgula.
 * `exists`/`notExists` em boolean = true/false.
 */
export interface FieldCondition {
  field: string;
  operator: ConditionOperator;
  value?: string;
}

export interface ColumnDef {
  /** Nome da coluna. Aceita caminho aninhado (ex: 'event.name') pra ler de uma relação 1:N carregada pelo repository — nesse caso não é ordenável. */
  name: string;
  label: string;
  type: FieldType;
  sortable?: boolean;
  /** Ordem de exibição (menor primeiro). Empate = ordem no array. */
  order?: number;
  /** Só pra type: 'multi-relation' — coluna vira badges buscados via GET /:resource/:id/:relation.resource. */
  relation?: RelationRef;
  /** Chave no component-registry, escape hatch para células fora do genérico. */
  component?: string;
  /** Máscaras (ver src/lib/mask.ts) — a exibida é a que melhor encaixa no tamanho do valor. */
  mask?: string[];
  /**
   * type: 'enum' — traduz o valor bruto (ex: "item") pro label (ex: "Item do pedido").
   * type: 'boolean' — rótulos do badge por value 'true'/'false' (ex: Ativo/Inativo); sem isso, Sim/Não.
   */
  options?: { label: string; value: string }[];
}

/**
 * Valor inicial de um filtro. String = valor fixo. `{ nextBy: '<campo data>' }` (só com
 * `relation`) = registro relacionado com o menor `<campo>` a partir de hoje; se não houver,
 * o mais recente (ex: soldiers filtrados pelo próximo evento).
 */
export type FilterDefaultValue = string | { nextBy: string };

export interface FilterDef {
  name: string;
  label: string;
  type: 'text' | 'number' | 'select' | 'date' | 'period';
  /** Ordem de exibição (menor primeiro). Empate = ordem no array. */
  order?: number;
  options?: { label: string; value: string }[];
  relation?: RelationRef;
  defaultValue?: FilterDefaultValue;
}

export interface FieldDef {
  name: string;
  label: string;
  type: FieldType;
  required?: boolean;
  /** Largura no grid de 12 colunas do formulário (1-12). Default: 4 (três campos por linha). */
  width?: number;
  /** @deprecated não usado no editor/renderer — largura real é `width`. Mantido só por compatibilidade de dados antigos. */
  colSpan?: { sm?: number; md?: number; lg?: number };
  /** Ordem de exibição (menor primeiro). Empate = ordem no array. */
  order?: number;
  section?: string;
  /** Só pra type: 'enum' — as opções do select. */
  options?: { label: string; value: string }[];
  relation?: RelationRef;
  /** Chave no component-registry, escape hatch para campos fora do genérico. */
  component?: string;
  /** Só pra type: 'file' — valor do atributo `accept` do input (ex: "application/pdf,image/*"). */
  accept?: string;
  /** Só pra type: 'file' — mostra um preview circular grande da imagem carregada, acima do campo. */
  avatarPreview?: boolean;
  /** Máscaras (ver src/lib/mask.ts e use-mask-input) — só no front, o backend recebe sem máscara. */
  mask?: string[];
  showOnCreate?: boolean;
  showOnEdit?: boolean;
  showOnView?: boolean;
  disabledOnEdit?: boolean;
  /** Valor inicial no formulário de criação (ex: "Ativo?" já marcado). Boolean sem isso = false. */
  defaultValue?: string | number | boolean;
  /** Campo só aparece se TODAS as condições forem verdadeiras. */
  visibleIf?: FieldCondition[];
  /** Campo fica desabilitado enquanto alguma condição for falsa. */
  enabledIf?: FieldCondition[];
}

export interface ScreenLayout {
  columns: ColumnDef[];
  filters: FilterDef[];
  fields: FieldDef[];
  /** Registros por página na listagem. Default: 10. */
  perPage?: number;
}

export const screenActionValues = [
  'browse',
  'create',
  'read',
  'update',
  'delete',
] as const;
export type ScreenAction = (typeof screenActionValues)[number];

export const screens = pgTable('screens', {
  id: uuid('id')
    .primaryKey()
    .$defaultFn(() => Bun.randomUUIDv7()),
  resource: text('resource').notNull().unique(),
  label: text('label').notNull(),
  pluralLabel: text('plural_label').notNull(),
  icon: text('icon'),
  menuOrder: integer('menu_order').notNull().default(0),
  menuGroup: text('menu_group'),
  showInMenu: boolean('show_in_menu').notNull().default(true),
  actions: text('actions')
    .array()
    .notNull()
    .default(screenActionValues as unknown as string[]),
  layout: jsonb('layout').notNull().$type<ScreenLayout>(),
  isActive: boolean('is_active').notNull().default(true),
  createdAt: timestamp('created_at')
    .notNull()
    .$defaultFn(() => new Date()),
  updatedAt: timestamp('updated_at')
    .$onUpdate(() => new Date())
    .notNull(),
  ...softDelete,
});

export type Screen = InferSelectModel<typeof screens>;
export type CreateScreenInput = InferInsertModel<typeof screens>;
export type UpdateScreenInput = Partial<CreateScreenInput>;
